import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { UzBadge, UzButton } from '@utazzo/ui';
import { EyeOff, GripVertical } from 'lucide-react';
import { useMemo } from 'react';
import { useWatch, type UseFormReturn } from 'react-hook-form';

import { useCategoryPageDestinationOptions } from '../use-category-pages';

import type {
  CategoryPageDestinationOption,
  CreateWebsiteCategoryPageRequest,
} from '@utazzo/shared';

/**
 * 2026-08-01. "Sorting" tab - the display order of the destination tiles
 * on this category page (the "Browse <category>" grid on the website).
 *
 * The list is seeded from the server with the tiles in the order the site
 * renders them TODAY, so the operator starts from reality. Until they drag
 * something the saved order stays empty, i.e. fully automatic: long haul
 * first, then short haul, then destinations the master hasn't classified.
 * The first drag freezes the whole visible list as the manual order.
 *
 * Rows are never added or removed here - this only positions the tiles the
 * category already has. Bind/unbind happens on the destination page itself.
 */
export const DestinationSortingEditor = ({
  form,
  pageId,
  disabled,
}: {
  form: UseFormReturn<CreateWebsiteCategoryPageRequest>;
  pageId?: string;
  disabled?: boolean;
}): React.JSX.Element => {
  const saved = useWatch({ control: form.control, name: 'destinationOrder' }) ?? [];
  const optionsQuery = useCategoryPageDestinationOptions(pageId);
  const options = useMemo(() => optionsQuery.data ?? [], [optionsQuery.data]);

  const isManual = saved.length > 0;

  // Saved order first, then anything the saved list doesn't mention (a
  // destination bound to the category after the order was last saved).
  // Ids that no longer resolve to a tile are dropped rather than rendered
  // as a blank row - saving then prunes them.
  const rows = useMemo((): CategoryPageDestinationOption[] => {
    const byId = new Map(options.map((o) => [o.destinationPageId, o]));
    const ordered: CategoryPageDestinationOption[] = [];
    const used = new Set<string>();
    for (const entry of saved) {
      const option = byId.get(entry.destinationPageId);
      if (!option || used.has(option.destinationPageId)) continue;
      used.add(option.destinationPageId);
      ordered.push(option);
    }
    for (const option of options) {
      if (!used.has(option.destinationPageId)) ordered.push(option);
    }
    return ordered;
  }, [options, saved]);

  const commit = (next: CategoryPageDestinationOption[]): void => {
    form.setValue(
      'destinationOrder',
      next.map((r) => ({ destinationPageId: r.destinationPageId })),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (e: DragEndEvent): void => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = rows.findIndex((r) => r.destinationPageId === String(active.id));
    const to = rows.findIndex((r) => r.destinationPageId === String(over.id));
    if (from < 0 || to < 0) return;
    // The whole visible list is written, not just the moved row: a partial
    // list would leave the untouched tiles on the automatic rule and they
    // would drift away from where the operator just saw them.
    commit(arrayMove(rows, from, to));
  };

  if (!pageId) {
    return (
      <p className="rounded-md border border-dashed border-zinc-200 px-3 py-6 text-center text-sm text-zinc-500">
        Save the page first - the destination tiles load once it exists.
      </p>
    );
  }

  return (
    <div className="max-w-3xl space-y-4">
      <div className="space-y-1">
        <p className="text-sm font-medium text-zinc-700">Destination tile order</p>
        <p className="text-xs text-zinc-500">
          The order of the destination cards on this category page. Drag a card to place it;
          everything you have not placed follows the automatic rule -{' '}
          <strong>long haul first, then short haul</strong>, then destinations with no haul set on
          the destination master.
        </p>
      </div>

      <div className="flex items-center justify-between gap-2">
        <UzBadge tone={isManual ? 'success' : 'secondary'} pill>
          {isManual ? `Manual order (${saved.length})` : 'Automatic: long haul first'}
        </UzBadge>
        {isManual && (
          <UzButton
            size="sm"
            variant="ghost"
            disabled={disabled}
            onClick={() =>
              form.setValue('destinationOrder', [], { shouldDirty: true, shouldValidate: true })
            }
          >
            Reset to automatic
          </UzButton>
        )}
      </div>

      {optionsQuery.isLoading ? (
        <p className="py-6 text-center text-xs text-zinc-500">Loading destinations…</p>
      ) : rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-zinc-200 px-3 py-6 text-center text-sm text-zinc-500">
          No destination pages are linked to this category yet. Bind them from the destination
          page's own “Category pages” picker, then come back here to order them.
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={rows.map((r) => r.destinationPageId)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {rows.map((row, index) => (
                <SortableTileRow
                  key={row.destinationPageId}
                  row={row}
                  index={index}
                  disabled={disabled}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

const HAUL_LABEL: Record<'LONG_HAUL' | 'SHORT_HAUL', string> = {
  LONG_HAUL: 'Long haul',
  SHORT_HAUL: 'Short haul',
};

const SortableTileRow = ({
  row,
  index,
  disabled,
}: {
  row: CategoryPageDestinationOption;
  index: number;
  disabled?: boolean;
}): React.JSX.Element => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.destinationPageId,
    disabled,
  });
  // The website drops a tile with no active packages, and an unpublished
  // destination page never renders - say so instead of leaving the operator
  // to wonder why a card they just moved isn't on the site.
  const hidden = !row.isPublished || row.packageCount === 0;

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={
        'flex items-center gap-3 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 ' +
        (isDragging ? 'z-10 opacity-60 shadow-md' : '')
      }
    >
      {!disabled && (
        <button
          type="button"
          className="cursor-grab rounded-md px-1 text-zinc-300 hover:bg-white hover:text-zinc-600 active:cursor-grabbing"
          aria-label={`Drag ${row.title} to reorder`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <span className="w-6 shrink-0 text-xs font-medium text-zinc-500">#{index + 1}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-zinc-800">{row.title}</p>
        <p className="truncate text-xs text-zinc-400">/{row.slug}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {hidden && (
          <span
            className="inline-flex items-center gap-1 text-xs text-amber-700"
            title={
              !row.isPublished
                ? 'This destination page is not published, so the tile is hidden on the website.'
                : 'No active packages behind this destination, so the website hides the tile.'
            }
          >
            <EyeOff className="h-3.5 w-3.5" />
            Hidden
          </span>
        )}
        <UzBadge tone="secondary" pill>
          {row.packageCount} pkg
        </UzBadge>
        <UzBadge tone={row.haul === 'LONG_HAUL' ? 'success' : 'secondary'} pill>
          {row.haul ? HAUL_LABEL[row.haul] : 'No haul'}
        </UzBadge>
      </div>
    </div>
  );
};
